import { environment } from 'environment';
import { roleHelpers, type MeiliTypes } from 'types';
import { create as vCreate } from 'zustand';
import { persist } from 'zustand/middleware';
import { devtools, getStorage } from './helpers';
import { useUser } from './user';

export async function NUKE() {
  const roles = useUser.getState()?.user?.roles;
  const resets: Promise<void>[] = [];

  if (roles?.length) {
    if (roleHelpers.access(roles, roleHelpers.acls.referralViewer))
      resets.push(import('web/components/hooks/useAdminReferrals').then(m => m.adminReferralDB.reset()));

    if (roleHelpers.access(roles, roleHelpers.acls.inventoryViewer)) {
      resets.push(
        import('web/components/hooks/useInventory').then(m => m.inventoryDb.reset()),
        import('web/components/hooks/useDeliveries').then(m => m.deliveryDb.reset()),
      );
    }

    resets.push(import('./referrals').then(m => m.usePartnerReferrals.getState()._reset()));
  }

  resets.push(import('./user').then(m => m.useUser.getState()._reset()));

  await Promise.all(resets);
}

interface AppState {
  searchKeys: MeiliTypes['keys'] & { _fetchedAt?: number };

  setSearchKeys: (searchKey: string) => void;

  _rehydrated: boolean;
  _setRehydrated: (_rehydrated: boolean) => void;

  fetchSearchKeys: () => Promise<MeiliTypes['keys']>;

  _reset: () => void;
}

export const app = vCreate<AppState>()(
  devtools(
    persist(
      (set, _get) => ({
        searchKeys: { public: '', _fetchedAt: 0 },

        setSearchKeys: (searchKey: string) => set(s => ({ ...s, searchKey })),

        _rehydrated: false,
        _setRehydrated: _rehydrated => set({ _rehydrated }),

        fetchSearchKeys: async () => {
          const keyRes = await import('networking').then(({ AppNet }) => AppNet.app.searchKeys());
          if (keyRes.success === false) return;

          set(s => ({ ...s, searchKeys: { ...keyRes.data, _fetchedAt: Date.now() } }));

          return keyRes.data;
        },

        _reset: () => {
          app.persist.clearStorage();
          set({ searchKeys: { public: '', _fetchedAt: 0 }, _rehydrated: true });
        },
      }),
      {
        name: `b4k-${environment.name}-app`,
        version: 1,
        onRehydrateStorage: () => state => state._setRehydrated(true),
        storage: getStorage(),
      },
    ),
    { name: 'app' },
  ),
);
